import { API_BASE_URL } from "../config/api";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import EngineerLayout from "../components/EngineerLayout";
import { appendAuthToken } from "../utils/auth";

const formatDate = (value) => {
  if (!value) return "-";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });
};

const formatDocumentStatus = (value = "") => {
  const normalized = String(value || "").trim();
  if (!normalized) return "Draft";
  return normalized.replace(/_/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
};

const getDocumentStatusStyles = (value = "") => {
  const normalized = String(value || "").trim().toLowerCase();
  if (["accepted", "paid", "completed", "invoiced"].includes(normalized)) {
    return "bg-emerald-100 text-emerald-700";
  }
  if (["sent", "partial", "partially_paid", "partially paid"].includes(normalized)) {
    return "bg-sky-100 text-sky-700";
  }
  if (["rejected", "overdue", "cancelled"].includes(normalized)) {
    return "bg-rose-100 text-rose-700";
  }
  if (normalized === "due today") {
    return "bg-amber-100 text-amber-700";
  }
  return "bg-amber-100 text-amber-700";
};

const getQuotationTotalAmount = (quotation = {}) => Number(quotation.grandTotal ?? quotation.subTotal ?? 0);

const getItemsSummary = (items) => {
  if (!items || !items.length) return "-";
  const firstItem = items[0].description || items[0].itemCode || "-";
  if (items.length > 1) {
    return `${firstItem} (+${items.length - 1} more)`;
  }
  return firstItem;
};

export default function CustomerGroupQuotations() {
  const { id } = useParams();
  const role = localStorage.getItem("role") || "";
  const location = useLocation();
  const navigate = useNavigate();

  const isEngineerRoute = location.pathname.startsWith("/engineer");
  const isEngineerView = role === "engineer" && isEngineerRoute;
  const isAccountsView = role === "accounts";
  const canManageCustomer = role === "admin";

  const [group, setGroup] = useState(null);
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingQuotation, setViewingQuotation] = useState(null);
  const [activeTab, setActiveTab] = useState("quotations");

  // Quotation search
  const [quotationSearch, setQuotationSearch] = useState("");

  // Product filters
  const [productSearch, setProductSearch] = useState("");
  const [productDateFrom, setProductDateFrom] = useState("");
  const [productDateTo, setProductDateTo] = useState("");
  const [productQuotationNo, setProductQuotationNo] = useState("");

  const quotationPagePath = canManageCustomer ? "/admin/quotations" : isAccountsView ? "/accounts/quotations" : "";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // 1. Fetch group details
        const groupRes = await axios.get(`${API_BASE_URL}/api/customer-groups/${id}`);
        const groupData = groupRes.data;
        setGroup(groupData);

        if (groupData && Array.isArray(groupData.customers) && groupData.customers.length > 0) {
          // 2. Fetch quotations for all customers in the group
          const customerIds = groupData.customers.map(c => c._id);
          const token = localStorage.getItem("token");
          const requests = customerIds.map(cid =>
            axios.get(`${API_BASE_URL}/api/quotations`, {
              params: { customerId: cid },
              headers: { Authorization: `Bearer ${token}` }
            })
          );
          const responses = await Promise.all(requests);
          const allQuotations = responses.flatMap(res => Array.isArray(res.data) ? res.data : []);
          setQuotations(allQuotations);
        } else {
          setQuotations([]);
        }
      } catch (err) {
        console.error("Failed to load group quotations", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const customerMap = useMemo(() => {
    const map = {};
    if (group && Array.isArray(group.customers)) {
      group.customers.forEach(c => {
        map[c._id] = c.name;
      });
    }
    return map;
  }, [group]);

  const handleBack = () => {
    navigate(
      isEngineerView
        ? `/engineer/customer-groups/${id}`
        : isAccountsView
        ? `/accounts/customer-groups/${id}`
        : `/customer-groups/${id}`
    );
  };

  const openQuotationPdf = (quotationId) => {
    window.open(appendAuthToken(API_BASE_URL + `/api/quotations/${quotationId}/pdf`), "_blank", "noopener,noreferrer");
  };

  const handleEditQuotation = (quotation) => {
    if (!quotationPagePath) return;
    navigate(quotationPagePath, {
      state: {
        quotationEdit: quotation,
        prefillKey: `quotation-edit-${quotation._id}-${Date.now()}`,
        fromPath: location.pathname
      }
    });
  };

  const handleDuplicateQuotation = (quotation) => {
    if (!quotationPagePath) return;
    navigate(quotationPagePath, {
      state: {
        duplicateQuotationPrefill: quotation,
        prefillKey: `duplicate-${quotation._id}-${Date.now()}`,
        fromPath: location.pathname
      }
    });
  };

  const handleReQuotation = (quotation) => {
    if (!quotationPagePath) return;
    navigate(quotationPagePath, {
      state: {
        reQuotationPrefill: quotation,
        prefillKey: `requote-${quotation._id}-${Date.now()}`,
        fromPath: location.pathname
      }
    });
  };

  const sortedGroupQuotations = useMemo(() => {
    return [...quotations].sort((a, b) => new Date(b.quotationDate || 0) - new Date(a.quotationDate || 0));
  }, [quotations]);

  const filteredGroupQuotations = useMemo(() => {
    let list = [...sortedGroupQuotations];
    const searchVal = quotationSearch.trim().toLowerCase();
    if (searchVal) {
      list = list.filter(q => 
        String(q.customerName || customerMap[q.customerId] || "").toLowerCase().includes(searchVal) ||
        String(q.quotationNo || "").toLowerCase().includes(searchVal)
      );
    }
    return list;
  }, [sortedGroupQuotations, quotationSearch, customerMap]);

  const allGroupProducts = useMemo(() => {
    const list = [];
    sortedGroupQuotations.forEach((quotation) => {
      const qDate = quotation.quotationDate;
      const qNo = quotation.quotationNo || "Draft";
      const qId = quotation._id;
      const custName = quotation.customerName || customerMap[quotation.customerId] || "-";
      
      if (Array.isArray(quotation.items)) {
        quotation.items.forEach((item) => {
          list.push({
            quotationId: qId,
            quotationDate: qDate,
            quotationNo: qNo,
            customerName: custName,
            productName: item.description || "",
            itemCode: item.itemCode || "",
            hsnCode: item.hsnCode || "",
            quantity: item.quantity || 0,
            unit: item.unit || "No",
            unitPrice: item.unitPrice || 0,
            totalPrice: item.totalAmount || 0,
            originalItem: item,
            originalQuotation: quotation
          });
        });
      }
    });
    return list;
  }, [sortedGroupQuotations, customerMap]);

  const filteredGroupProducts = useMemo(() => {
    let list = [...allGroupProducts];

    // Search by Product Name, Item Code, HSN Code, Quotation Number, Customer Name
    const searchVal = productSearch.trim().toLowerCase();
    if (searchVal) {
      list = list.filter((item) => 
        String(item.productName).toLowerCase().includes(searchVal) ||
        String(item.itemCode).toLowerCase().includes(searchVal) ||
        String(item.hsnCode).toLowerCase().includes(searchVal) ||
        String(item.quotationNo).toLowerCase().includes(searchVal) ||
        String(item.customerName).toLowerCase().includes(searchVal)
      );
    }

    // Filter by Date Range
    if (productDateFrom) {
      const fromDate = new Date(productDateFrom);
      list = list.filter((item) => {
        const itemDate = new Date(item.quotationDate);
        return itemDate >= fromDate;
      });
    }

    if (productDateTo) {
      const toDate = new Date(productDateTo);
      toDate.setHours(23, 59, 59, 999);
      list = list.filter((item) => {
        const itemDate = new Date(item.quotationDate);
        return itemDate <= toDate;
      });
    }

    // Filter by Quotation Number
    const qNoFilter = productQuotationNo.trim().toLowerCase();
    if (qNoFilter) {
      list = list.filter((item) => 
        String(item.quotationNo).toLowerCase().includes(qNoFilter)
      );
    }

    return list;
  }, [allGroupProducts, productSearch, productDateFrom, productDateTo, productQuotationNo]);

  if (loading) {
    const loader = <div className="p-8 text-center text-sm text-slate-500">Loading group history...</div>;
    return isEngineerView ? <EngineerLayout>{loader}</EngineerLayout> : loader;
  }

  if (!group) {
    const errorMsg = (
      <div className="p-8 text-center">
        <p className="text-sm text-red-500 font-semibold">Customer Group not found.</p>
        <button onClick={handleBack} className="mt-4 rounded bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-800">
          Back to Group
        </button>
      </div>
    );
    return isEngineerView ? <EngineerLayout>{errorMsg}</EngineerLayout> : errorMsg;
  }

  const content = (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Header banner */}
      <div className="border-b border-slate-200 bg-white px-4 py-5 shadow-sm sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-[95%] xl:max-w-[1600px] items-start gap-4">
          <button
            type="button"
            onClick={handleBack}
            className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-sm transition hover:bg-slate-50"
            aria-label="Back to group profile"
          >
            <span className="text-xl leading-none">&larr;</span>
          </button>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-blue-700">Group Quotation & Product History</p>
            <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-950 sm:text-3xl">{group.groupName}</h1>
            <p className="mt-1 text-sm text-slate-500">Aggregated quotation records for all members of this customer group.</p>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[95%] xl:max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
          {/* Sub-tab Switcher Bar */}
          <div className="flex items-center gap-2 border-b border-slate-100 pb-3 mb-6">
            <button
              type="button"
              onClick={() => setActiveTab("quotations")}
              className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                activeTab === "quotations"
                  ? "bg-sky-600 text-white shadow-sm border-sky-600"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Quotations
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("products")}
              className={`rounded-2xl px-4 py-2.5 text-xs font-semibold transition ${
                activeTab === "products"
                  ? "bg-sky-600 text-white shadow-sm border-sky-600"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              Products
            </button>
          </div>

          {activeTab === "quotations" ? (
            <>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between mb-6">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Quotation History</h2>
                  <p className="mt-1 text-sm text-slate-500">Combined quotation list for all group members.</p>
                </div>
                <div className="relative w-full sm:w-72">
                  <input
                    type="text"
                    placeholder="Search by customer or quotation number..."
                    value={quotationSearch}
                    onChange={(e) => setQuotationSearch(e.target.value)}
                    className="h-10 w-full rounded-[6px] border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-850 outline-none transition placeholder:text-slate-400 focus:border-sky-400 focus:ring-2 focus:ring-sky-100"
                  />
                  <span className="material-symbols-outlined absolute left-3 top-2.5 text-slate-400 text-lg">search</span>
                </div>
              </div>

              <div className="overflow-x-auto">
                {filteredGroupQuotations.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-350 bg-slate-50 px-4 py-10 text-center text-slate-500">
                    No quotations found.
                  </div>
                ) : (
                  <table className="w-full min-w-[980px] text-left text-sm border-collapse">
                    <thead className="bg-slate-100 text-slate-700 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3.5 font-semibold">Quotation Date</th>
                        <th className="px-4 py-3.5 font-semibold">Customer Name</th>
                        <th className="px-4 py-3.5 font-semibold">Quotation Number</th>
                        <th className="px-4 py-3.5 font-semibold">Items</th>
                        <th className="px-4 py-3.5 font-semibold">Total Amount</th>
                        <th className="px-4 py-3.5 font-semibold">Status</th>
                        <th className="px-4 py-3.5 font-semibold">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-150">
                      {filteredGroupQuotations.map((quotation) => {
                        const custName = quotation.customerName || customerMap[quotation.customerId] || "-";
                        return (
                          <tr key={quotation._id} className="hover:bg-slate-50/50 transition-colors">
                            <td className="px-4 py-3 text-slate-700">{formatDate(quotation.quotationDate)}</td>
                            <td className="px-4 py-3 font-semibold text-slate-900">{custName}</td>
                            <td className="px-4 py-3 font-medium text-slate-700">{quotation.quotationNo || "-"}</td>
                            <td className="px-4 py-3 text-slate-600">{getItemsSummary(quotation.items)}</td>
                            <td className="px-4 py-3 text-slate-950 font-bold">Rs {getQuotationTotalAmount(quotation).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                            <td className="px-4 py-3">
                              <StatusBadge label={formatDocumentStatus(quotation.status)} className={getDocumentStatusStyles(quotation.status)} />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <ActionButton label="View" tone="sky" onClick={() => setViewingQuotation(quotation)} />
                                <ActionDropdown
                                  label="Actions"
                                  items={[
                                    {
                                      label: "Edit",
                                      icon: (
                                        <svg className="h-4 w-4 text-amber-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                        </svg>
                                      ),
                                      onClick: () => handleEditQuotation(quotation),
                                      hidden: !(canManageCustomer || isAccountsView)
                                    },
                                    {
                                      label: "PDF",
                                      icon: (
                                        <svg className="h-4 w-4 text-sky-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                      ),
                                      onClick: () => openQuotationPdf(quotation._id)
                                    },
                                    {
                                      label: "Re-Quotation",
                                      icon: (
                                        <svg className="h-4 w-4 text-blue-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                      ),
                                      onClick: () => handleReQuotation(quotation),
                                      hidden: !(canManageCustomer || isAccountsView)
                                    }
                                  ]}
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between mb-4">
                <div>
                  <h2 className="text-xl font-bold text-slate-900">Quoted Products</h2>
                  <p className="mt-1 text-sm text-slate-500">List of all items ever quoted for this customer group.</p>
                </div>
              </div>

              {/* Filters Block */}
              <div className="grid gap-4 md:grid-cols-4 items-end mb-6 bg-slate-50 p-4 border border-slate-200 rounded-3xl">
                {/* Search Input */}
                <div className="flex flex-col gap-1 md:col-span-2">
                  <label className="text-xs font-bold text-slate-600">Search Products</label>
                  <div className="relative">
                    <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400">
                      <svg xmlns="http://www.w3.org/2500/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z" />
                      </svg>
                    </span>
                    <input
                      type="text"
                      placeholder="Search by product name, item code, HSN, customer, quotation..."
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 bg-white py-2.5 pl-9 pr-4 text-xs text-slate-700 outline-none transition focus:border-sky-400 focus:bg-white"
                    />
                  </div>
                </div>

                {/* Date From */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">From Date</label>
                  <input
                    type="date"
                    value={productDateFrom}
                    onChange={(e) => setProductDateFrom(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  />
                </div>

                {/* Date To */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">To Date</label>
                  <input
                    type="date"
                    value={productDateTo}
                    onChange={(e) => setProductDateTo(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  />
                </div>

                {/* Quotation No filter */}
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-bold text-slate-600">Filter by Quotation No</label>
                  <input
                    type="text"
                    placeholder="e.g. GS/AI/2605-1"
                    value={productQuotationNo}
                    onChange={(e) => setProductQuotationNo(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 outline-none transition focus:border-sky-400"
                  />
                </div>
                
                {/* Clear Button */}
                {(productSearch || productDateFrom || productDateTo || productQuotationNo) && (
                  <div className="md:col-span-4 flex justify-end">
                    <button
                      type="button"
                      onClick={() => {
                        setProductSearch("");
                        setProductDateFrom("");
                        setProductDateTo("");
                        setProductQuotationNo("");
                      }}
                      className="text-xs font-bold text-red-500 hover:text-red-700"
                    >
                      Clear Filters
                    </button>
                  </div>
                )}
              </div>

              {/* Products Table */}
              <div className="overflow-x-auto">
                {filteredGroupProducts.length === 0 ? (
                  <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-10 text-center text-slate-500">
                    No products found matching the criteria.
                  </div>
                ) : (
                  <table className="w-full min-w-[1100px] text-left text-sm border-collapse">
                    <thead className="bg-slate-100 text-slate-700 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3.5 font-semibold">Quotation Date</th>
                        <th className="px-4 py-3.5 font-semibold">Customer Name</th>
                        <th className="px-4 py-3.5 font-semibold">Quotation Number</th>
                        <th className="px-4 py-3.5 font-semibold">Product Name</th>
                        <th className="px-4 py-3.5 font-semibold">Item Code</th>
                        <th className="px-4 py-3.5 font-semibold">HSN Code</th>
                        <th className="px-4 py-3.5 text-right font-semibold">Quantity</th>
                        <th className="px-4 py-3.5 font-semibold">Unit</th>
                        <th className="px-4 py-3.5 text-right font-semibold">Unit Price</th>
                        <th className="px-4 py-3.5 text-right font-semibold">Total Price</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-150">
                      {filteredGroupProducts.map((p, idx) => (
                        <tr key={`${p.quotationId}-${idx}`} className="hover:bg-slate-50/50 transition-colors">
                          <td className="px-4 py-3 text-slate-700">{formatDate(p.quotationDate)}</td>
                          <td className="px-4 py-3 font-semibold text-slate-900">{p.customerName}</td>
                          <td className="px-4 py-3">
                            <button
                              type="button"
                              onClick={() => setViewingQuotation(p.originalQuotation)}
                              className="font-medium text-blue-600 hover:underline hover:text-blue-800 text-left"
                            >
                              {p.quotationNo}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-slate-800 font-medium whitespace-pre-wrap">{p.productName}</td>
                          <td className="px-4 py-3 font-mono text-xs text-slate-655">{p.itemCode || "-"}</td>
                          <td className="px-4 py-3 text-slate-550">{p.hsnCode || "-"}</td>
                          <td className="px-4 py-3 text-right text-slate-900 font-semibold">{p.quantity}</td>
                          <td className="px-4 py-3 text-slate-600">{p.unit || "No"}</td>
                          <td className="px-4 py-3 text-right text-slate-700">₹{(p.unitPrice || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                          <td className="px-4 py-3 text-right text-slate-950 font-bold">₹{(p.totalPrice || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </>
          )}
        </section>
      </div>

      {viewingQuotation && (
        <QuotationViewerModal
          quotation={viewingQuotation}
          onClose={() => setViewingQuotation(null)}
          onDuplicate={handleDuplicateQuotation}
          onPdf={() => openQuotationPdf(viewingQuotation._id)}
          onEdit={(canManageCustomer || isAccountsView) ? () => {
            handleEditQuotation(viewingQuotation);
            setViewingQuotation(null);
          } : null}
          onReQuotation={(canManageCustomer || isAccountsView) ? () => {
            handleReQuotation(viewingQuotation);
            setViewingQuotation(null);
          } : null}
        />
      )}
    </div>
  );

  return isEngineerView ? <EngineerLayout>{content}</EngineerLayout> : content;
}

function StatusBadge({ label, className }) {
  return (
    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${className}`}>
      {label}
    </span>
  );
}

function ActionDropdown({ label = "Actions", items = [], align = "right" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const updateCoords = useCallback(() => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const openUp = spaceBelow < 210 && spaceAbove > spaceBelow;

    let left = null;
    let right = null;

    if (align === "left") {
      left = Math.max(8, rect.left);
    } else {
      const rightDist = window.innerWidth - rect.right;
      if (rect.right < 210) {
        left = Math.max(8, rect.left);
      } else {
        right = Math.max(8, rightDist);
      }
    }

    if (openUp) {
      setCoords({
        top: null,
        bottom: Math.max(8, window.innerHeight - rect.top + 6),
        left,
        right,
        maxHeight: Math.min(360, Math.max(140, spaceAbove - 20))
      });
    } else {
      setCoords({
        top: Math.max(8, rect.bottom + 6),
        bottom: null,
        left,
        right,
        maxHeight: Math.min(360, Math.max(140, spaceBelow - 20))
      });
    }
  }, [align]);

  const handleToggle = (e) => {
    e.stopPropagation();
    if (!isOpen) {
      updateCoords();
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    updateCoords();

    const handleClickOutside = (event) => {
      if (
        (buttonRef.current && buttonRef.current.contains(event.target)) ||
        (menuRef.current && menuRef.current.contains(event.target))
      ) {
        return;
      }
      setIsOpen(false);
    };

    const handleScrollOrResize = (event) => {
      if (menuRef.current && menuRef.current.contains(event.target)) {
        return;
      }
      setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("scroll", handleScrollOrResize, true);
    window.addEventListener("resize", handleScrollOrResize);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("scroll", handleScrollOrResize, true);
      window.removeEventListener("resize", handleScrollOrResize);
    };
  }, [isOpen, updateCoords]);

  const visibleItems = items.filter((item) => !item.hidden);
  if (visibleItems.length === 0) return null;

  return (
    <div className="relative inline-block text-left">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center gap-1.5 rounded-2xl border border-slate-300 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 hover:border-slate-400 focus:outline-none cursor-pointer"
      >
        <span>{label}</span>
        <span className={`text-[9px] text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`}>▼</span>
      </button>

      {isOpen && coords && createPortal(
        <div
          ref={menuRef}
          style={{
            position: "fixed",
            ...(coords.top !== null ? { top: `${coords.top}px` } : {}),
            ...(coords.bottom !== null ? { bottom: `${coords.bottom}px` } : {}),
            ...(coords.left !== null ? { left: `${coords.left}px` } : {}),
            ...(coords.right !== null ? { right: `${coords.right}px` } : {}),
            maxHeight: `${coords.maxHeight}px`,
            zIndex: 99999
          }}
          className="min-w-[170px] w-max max-w-[240px] overflow-y-auto rounded-xl border border-slate-200 bg-white p-1.5 shadow-xl animate-fadeIn divide-y divide-slate-100"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-0.5 space-y-0.5">
            {visibleItems.map((item, idx) => (
              <button
                key={idx}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  item.onClick?.();
                }}
                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-semibold transition-colors flex items-center gap-2.5 cursor-pointer ${
                  item.danger
                    ? "text-rose-600 hover:bg-rose-50"
                    : item.highlight
                    ? "text-blue-700 hover:bg-blue-50"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
              >
                {item.icon && <span className="text-sm shrink-0">{item.icon}</span>}
                <span className="truncate">{item.label}</span>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

function ActionButton({ label, tone, onClick }) {
  const tones = {
    sky: "bg-sky-600 hover:bg-sky-700 text-white",
    amber: "bg-amber-500 hover:bg-amber-600 text-white",
    emerald: "bg-emerald-600 hover:bg-emerald-700 text-white",
    red: "bg-red-600 hover:bg-red-700 text-white",
    violet: "bg-violet-600 hover:bg-violet-700 text-white"
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-2xl px-4 py-2 text-sm font-semibold transition ${tones[tone] || tones.sky}`}
    >
      {label}
    </button>
  );
}

function QuotationViewerModal({ quotation, onClose, onDuplicate, onPdf, onEdit, onReQuotation }) {
  if (!quotation) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div
        className="relative w-full max-w-4xl rounded-[24px] border border-slate-200 bg-white p-6 shadow-2xl flex flex-col max-h-[90vh]"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between border-b border-slate-100 pb-4 shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-slate-900">Quotation Details</h2>
              <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${
                quotation.status === "accepted" || quotation.status === "invoiced" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                quotation.status === "sent" ? "bg-blue-50 text-blue-700 border-blue-200" :
                quotation.status === "rejected" ? "bg-red-50 text-red-700 border-red-200" :
                quotation.status === "expired" ? "bg-amber-50 text-amber-700 border-amber-200" :
                "bg-slate-50 text-slate-600 border-slate-200"
              }`}>
                {quotation.status}
              </span>
            </div>
            <p className="mt-1 text-sm text-slate-500 font-semibold">{quotation.quotationNo || "Draft Quotation"}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full bg-slate-100 p-2 text-slate-500 transition hover:bg-slate-200 hover:text-slate-800 focus:outline-none"
            style={{ width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: "0" }}
          >
            ✕
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="mt-4 overflow-y-auto space-y-5 flex-1 pr-1">
          {/* Metadata Cards */}
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-[16px] border border-slate-100 bg-slate-50/50 p-4 space-y-2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Customer Details</h3>
              <div className="text-sm space-y-1">
                <p className="font-bold text-slate-900">{quotation.customerName || "-"}</p>
                {quotation.contactPerson && <p className="text-slate-600"><span className="font-semibold text-slate-500">Contact:</span> {quotation.contactPerson}</p>}
                {quotation.phone && <p className="text-slate-600"><span className="font-semibold text-slate-500">Phone:</span> {quotation.phone}</p>}
                {quotation.email && <p className="text-slate-600"><span className="font-semibold text-slate-500">Email:</span> {quotation.email}</p>}
                {quotation.gstNumber && <p className="text-slate-600"><span className="font-semibold text-slate-500">GST:</span> {quotation.gstNumber}</p>}
                {quotation.billingAddress && <p className="text-slate-600 mt-1 whitespace-pre-wrap"><span className="font-semibold text-slate-500">Billing Address:</span><br/>{quotation.billingAddress}</p>}
              </div>
            </div>

            <div className="rounded-[16px] border border-slate-100 bg-slate-50/50 p-4 space-y-2">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Quotation Information</h3>
              <div className="text-sm space-y-1">
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Date:</span> {quotation.quotationDate ? new Date(quotation.quotationDate).toLocaleDateString("en-GB") : "-"}</p>
                <p className="text-slate-600"><span className="font-semibold text-slate-500">Valid Until:</span> {quotation.validUntil ? new Date(quotation.validUntil).toLocaleDateString("en-GB") : "-"}</p>
                {quotation.referenceNo && <p className="text-slate-600"><span className="font-semibold text-slate-500">Reference No:</span> {quotation.referenceNo}</p>}
                {quotation.subject && <p className="text-slate-600"><span className="font-semibold text-slate-500">Subject:</span> {quotation.subject}</p>}
                {quotation.deliveryTime && <p className="text-slate-600"><span className="font-semibold text-slate-500">Delivery Time:</span> {quotation.deliveryTime}</p>}
                {quotation.paymentTerms && <p className="text-slate-600"><span className="font-semibold text-slate-500">Payment Terms:</span> {quotation.paymentTerms}</p>}
                {quotation.warranty && <p className="text-slate-600"><span className="font-semibold text-slate-500">Warranty:</span> {quotation.warranty}</p>}
                {(quotation.gstType === "sez_supply" || quotation.lutRefNo) && (
                  <p className="text-slate-600"><span className="font-semibold text-slate-500">LUT:</span> <span className="font-bold text-slate-800">TAX EXEMPTED</span>{quotation.lutRefNo ? ` • ${quotation.lutRefNo}` : ""}</p>
                )}
              </div>
            </div>
          </div>

          {/* Products / Items Table */}
          <div className="space-y-2">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1">
              <span>🛒</span> Quoted Products
            </h3>
            <div className="overflow-x-auto rounded-[12px] border border-slate-200 bg-white">
              <table className="w-full min-w-[700px] text-left text-xs border-collapse">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200 text-slate-600 font-bold">
                    <th className="p-2.5 text-center border-r border-slate-100 w-10">#</th>
                    <th className="p-2.5 border-r border-slate-100 w-28">Item Code</th>
                    <th className="p-2.5 border-r border-slate-100 w-24">HSN Code</th>
                    <th className="p-2.5 border-r border-slate-100">Description</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-16">Qty</th>
                    <th className="p-2.5 border-r border-slate-100 w-16">Unit</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">List Price</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">Discount</th>
                    <th className="p-2.5 text-right border-r border-slate-100 w-24">Unit Price</th>
                    <th className="p-2.5 text-right w-24">Total Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {(quotation.items || []).map((item, idx) => (
                    <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50/50 align-middle">
                      <td className="p-2.5 text-center border-r border-slate-100 text-slate-400 font-semibold">{idx + 1}</td>
                      <td className="p-2.5 border-r border-slate-100 font-mono font-medium text-slate-600 uppercase">{item.itemCode || "-"}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-500">{item.hsnCode || "-"}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-800 font-medium whitespace-pre-wrap">{item.description}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-900 font-semibold">{item.quantity}</td>
                      <td className="p-2.5 border-r border-slate-100 text-slate-600">{item.unit || "NA"}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-600 font-medium">₹{(item.listPrice ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-red-600 font-medium">
                        {item.discountValue ? (item.discountType === "amount" ? `₹${item.discountValue.toLocaleString("en-IN")}` : `${item.discountValue}%`) : "-"}
                      </td>
                      <td className="p-2.5 text-right border-r border-slate-100 text-slate-700 font-semibold">₹{(item.unitPrice ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                      <td className="p-2.5 text-right text-slate-900 font-bold">₹{(item.totalAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  ))}
                  {(!quotation.items || !quotation.items.length) && (
                    <tr>
                      <td colSpan="10" className="p-8 text-center text-slate-400">No products listed.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bottom Area: Terms & Summary */}
          <div className="grid gap-4 md:grid-cols-12">
            <div className="md:col-span-7 rounded-[16px] border border-slate-100 p-4 space-y-3">
              <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Terms & Remarks</h4>
              <div className="text-xs text-slate-600 space-y-2 leading-relaxed">
                {quotation.terms && <p><strong>Terms:</strong> {quotation.terms}</p>}
                {quotation.notes && <p><strong>Notes:</strong> {quotation.notes}</p>}
              </div>
            </div>

            <div className="md:col-span-5 rounded-[16px] border border-slate-200 bg-slate-50/50 p-4 space-y-2">
              <div className="flex justify-between text-xs font-bold text-slate-800">
                <span>Sub Total:</span>
                <span className="font-bold text-slate-900">₹{(quotation.subTotal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              {(quotation.overallDiscountPercent ?? 0) > 0 && (
                <>
                  <div className="flex justify-between text-xs text-red-600 font-semibold">
                    <span>Overall Discount ({quotation.overallDiscountPercent}%):</span>
                    <span>-₹{(quotation.overallDiscountAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex justify-between text-xs text-slate-600 font-semibold">
                    <span>After Discount:</span>
                    <span>₹{((quotation.subTotal ?? 0) - (quotation.overallDiscountAmount ?? 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between text-xs text-slate-500 font-semibold">
                <span>GST:</span>
                <span>₹{(quotation.gstAmount ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm font-bold text-slate-800 border-t border-slate-200 pt-2">
                <span>Grand Total:</span>
                <span className="text-blue-600">₹{(quotation.grandTotal ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="mt-6 flex items-center justify-end gap-2 border-t border-slate-100 pt-4 shrink-0">
          <button
            type="button"
            onClick={onPdf}
            className="rounded-[12px] bg-sky-600 hover:bg-sky-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
          >
            <span>📄</span> View PDF
          </button>
          {onEdit && (
            <button
              type="button"
              onClick={onEdit}
              className="rounded-[12px] bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              <span>✏️</span> Edit
            </button>
          )}
          {onReQuotation && (
            <button
              type="button"
              onClick={onReQuotation}
              className="rounded-[12px] bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
            >
              <span>🔄</span> Re-Quotation
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              onDuplicate(quotation);
              onClose();
            }}
            className="rounded-[12px] bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 text-xs font-semibold flex items-center gap-1.5 transition"
          >
            <span>👥</span> Duplicate to Other Customer
          </button>
          <button
            type="button"
            onClick={onClose}
            className="rounded-[12px] border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 px-4 py-2 text-xs font-semibold transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
